import { Button, type ButtonProps } from '@unifyapps/ui/components/Button';
import { MenuList } from '@unifyapps/ui/components/Menu';
import type { MenuListProps } from '@unifyapps/ui/components/Menu/MenuList';
import { Popover } from '@unifyapps/ui/components/Popover';
import { clsx } from 'clsx';
import type { SyntheticEvent } from 'react';
import { forwardRef } from 'react';

type Props = {
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  hasOnlyIcon?: boolean;
  className?: string;
  menuListClassName?: MenuListProps['className'];
  menuListStyle?: MenuListProps['style'];
  menuListSize?: MenuListProps['size'];
  popoverContentClassName?: string;
  popoverContentStyle?: React.CSSProperties;
  disableStyleAdaptation?: boolean;
} & Pick<
  ButtonProps,
  'color' | 'size' | 'variant' | 'startDecoratorComponent' | 'endDecoratorComponent' | 'children'
> &
  Pick<MenuListProps, 'items' | 'onAction'>;

const ICON_ONLY_STYLES = { '--button-padding-x': 'var(--button-padding-y)' } as React.CSSProperties;

export const DropdownButtonGroup = forwardRef<HTMLButtonElement, Props>(
  (
    {
      onOpenChange,
      open,
      hasOnlyIcon,
      className,
      color,
      size,
      variant,
      startDecoratorComponent,
      endDecoratorComponent,
      children,
      menuListClassName,
      items,
      onAction,
      menuListStyle,
      menuListSize,
      popoverContentClassName,
      popoverContentStyle,
      disableStyleAdaptation,
      ...restProps
    },
    ref,
  ) => {
    return (
      <Popover onOpenChange={onOpenChange} open={open}>
        {({ onClose }) => (
          <>
            <Popover.Button>
              <Button
                className={clsx({ '!min-w-[unset]': hasOnlyIcon }, className)}
                color={color}
                endDecoratorComponent={endDecoratorComponent}
                ref={ref}
                size={size}
                startDecoratorComponent={startDecoratorComponent}
                style={hasOnlyIcon ? ICON_ONLY_STYLES : undefined}
                variant={variant}
                {...restProps}
              >
                {children}
              </Button>
            </Popover.Button>
            <Popover.Content
              className={popoverContentClassName}
              disableStyleAdaptation={disableStyleAdaptation}
              style={popoverContentStyle}
            >
              <MenuList
                className={menuListClassName}
                items={items}
                onAction={(id: string, event: SyntheticEvent) => {
                  onAction?.(id, event);
                  onClose?.();
                }}
                size={menuListSize}
                style={menuListStyle}
              />
            </Popover.Content>
          </>
        )}
      </Popover>
    );
  },
);
